import React, {
   CSSProperties,
   forwardRef,
   ReactElement,
   Ref, useCallback,
   useImperativeHandle, useMemo,
   useRef,
   useState
} from 'react';
import {
   StackPanel,
   StackPanelChangeEvent,
   StackPanelRefType
} from '../../components/layout/stackpanel';
import { NavBar, NavBarBackButton } from '../../components/controls/navbar';
import { CardPanelItem } from '../../components/layout/cardpanel';
import { BorderPanel } from '../../components/layout/borderpanel';
import { childrenOfType, findChildOfType } from '../../utils';

/**
 * {@docCategory NavPanel}
 */
export interface NavPanelProps {
   /**
    * Title displayed in the header. Can be overridden by each NavPanelItem.
    */
   title: string;

   /**
    * Component to be displayed in the left section of the header when the user is on the root item.
    */
   rootNavBarLeftItem?: ReactElement;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;
}

/**
 * {@docCategory NavPanel}
 */
export interface NavPanelItemProps {
   /**
    * The id of the item.
    */
   id: string;

   /**
    * An optional item to display on the right side of the NavBar when
    * this item is the current item.
    */
   rightNavBarItem?: ReactElement;

   /**
    * Override the title of the NavPanel when this NavPanelItem is selected
    */
   titleOverride?: string;

   /**
    * Set to true to unmount the item content from the DOM when inactive.
    */
   destroyWhenInactive?: boolean;

   /**
    * Set to true to render the item content on initial render.
    */
   forceRender?: boolean;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;
}

/**
 * {@docCategory NavPanel}
 */
export interface NavPanelRefType {
   back: () => void;
   go: (id: string) => void;
}

export declare type NavPanelPropsWithChildren = React.PropsWithChildren<NavPanelProps>;

export declare type NavPanelItemPropsWithChildren = React.PropsWithChildren<NavPanelItemProps>;

export const NavPanel = forwardRef<NavPanelRefType | undefined, NavPanelPropsWithChildren>(
   (props: NavPanelPropsWithChildren, ref: Ref<NavPanelRefType | undefined>) => {
      const stackPanelRef = useRef<StackPanelRefType>();
      const [backDisabled, setBackDisabled] = useState<boolean>(true);
      const [activeCardId, setActiveCardId] = useState<string>();

      const onChange = useCallback((event: StackPanelChangeEvent) => {
         setBackDisabled(event.backEmpty);
         setActiveCardId(event.newCardId);
      },[]);

      const onBack = () => {
         stackPanelRef.current?.back();
      };

      useImperativeHandle(ref, () => ({
         back: () => {
            stackPanelRef.current?.back();
         },
         go: (id: string) => {
            stackPanelRef.current?.go(id);
         }
      } as StackPanelRefType));

      const navBar = useMemo(()=>(<NavBar
         leftContent={backDisabled ? props.rootNavBarLeftItem : backButton(onBack)}
         title={title(props.title, props.children, activeCardId)}
         rightContent={rightButton(props.children, activeCardId)}
      />),[props.title, props.children, props.rootNavBarLeftItem, activeCardId, backDisabled]);

      return (
         <BorderPanel
            top={navBar}
         >
            <StackPanel
               ref={stackPanelRef}
               onChange={onChange}
               className={`uf-nav-panel ${props.className || ''}`}
               style={props.style}
            >
               {childrenOfType(props.children, NavPanelItem).map((child => {
                  const childElement = child as ReactElement;
                  const childProps = childElement.props as NavPanelItemPropsWithChildren;

                  return (<CardPanelItem
                     key={childProps.id}
                     id={childProps.id}
                     className={childProps.className}
                     style={childProps.style}
                     forceRender={childProps.forceRender}
                     destroyWhenInactive={childProps.destroyWhenInactive}
                  >
                     {childProps.children}
                  </CardPanelItem>);
               }))}
            </StackPanel>
         </BorderPanel>
      );
   });

export class NavPanelItem extends React.Component<React.PropsWithChildren<NavPanelItemProps>> {
}

const backButton = (onClick: () => void) => {
   return (
      <NavBarBackButton onClick={onClick}/>
   );
};

const title = (title: string, children?: React.ReactNode, activeCardId?: string) => {
   if (activeCardId) {
      const activeNavPanelItem = findChildOfType<NavPanelItemProps>(children, NavPanelItem, child => {
         return child.props.id === activeCardId;
      });

      if (activeNavPanelItem && activeNavPanelItem.props.titleOverride) {
         return activeNavPanelItem.props.titleOverride;
      }
   }

   return title;
};

const rightButton = (children?: React.ReactNode, activeCardId?: string) => {
   if (activeCardId) {
      const activeNavPanelItem = findChildOfType<NavPanelItemProps>(children, NavPanelItem, child => {
         return child.props.id === activeCardId;
      });

      if (activeNavPanelItem && activeNavPanelItem.props.rightNavBarItem) {
         return activeNavPanelItem.props.rightNavBarItem;
      }
   }

   return undefined;
};
